import Tooltip from "../../common/Tooltip.jsx";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
  useFirestoreGeneral,
  useFirestoreGeneralOrderBy,
  useFirestoreGeneralTwoOrderBy,
  useFirestoreUsersApproved,
} from "../../../firebase/useFirestore.js";
import { db } from "../../../firebase/config.js";
import { v4 as uuid } from "uuid";
import { client } from "../../../hooks/Client.js";
import { SavedIcon } from "../../../StateManagment/SavedIcon.js";
import Location from "../../../hooks/Location.js";
import { useContext, useState, useEffect, useMemo } from "react";
import useSettings from "../../../hooks/Settings.jsx";
import { householdPlanningEnabled } from "../../../helpers/board/resolveBoardView";
import { useHistory } from "react-router-dom";
import { Auth } from "../../../StateManagment/Auth.js";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import DeleteModal from "../../common/DeleteModal.jsx";
import Modal from "../../common/Modal.jsx";
import firebase from "../../../firebase/compat";
import UserRoleMeta from "../../settings/UserRoleMeta.jsx";
import SelectTeamMembers from "../../tasks/SelectTeamMembers.jsx";
import getTeamMembers from "../../../helpers/getTeamMembers.js";
import Hostname from "../../../hooks/Hostname";
import AddTaskModalContent from "./AddTaskModalContent.jsx";
import TaskEditor from "../../tasks/TaskEditor.jsx";
import { useTaskDTO } from "../../../hooks/firebase/TaskDto.jsx";
import resolveAppointmentKinds, { defaultVisitTitle } from "../../../helpers/tasks/resolveAppointmentKinds.js";
import Table from "../../common/table/Table.jsx";
import TableCell from "../../common/table/TableCell.jsx";
import TableTitleCell from "../../common/table/TableTitleCell.jsx";
import { getAdressLabels } from "../../../helpers/getAdressDescription.js";
import afterHouseVisitConfirmed from "../../../helpers/adressHelpers/afterHouseVisitConfirmed.js";
import readHouseholdField from "../../../helpers/adressHelpers/householdFields.js";
import buildConfirmationMail from "../../../helpers/adressHelpers/buildConfirmationMail.js";
import buildPlatformSender from "../../../helpers/mail/platformSender.js";
import confirmationMailAddresses from "../../../helpers/mail/confirmationMailAddresses.js";
import ReadOnlyGuard from "../../common/ReadOnlyGuard";
import softDeleteDoc from '../../../helpers/softDeleteDoc';

const Tasks = ({ adressNavigation, adressTitle }) => {
  // Context
  const [, setSaved, , setSavedMessage] = useContext(SavedIcon);
  const [auth] = useContext(Auth);

  // State
  const [dragArray, setDragArray] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteName, setDeleteName] = useState("deze taak");
  const [deleteDocid, setDeleteDocid] = useState("");
  const [teamMemberModal, setTeamMemberModal] = useState(false);
  const [adressEmail, setAdressEmail] = useState("");
  const [adressName, setAdressName] = useState("");
  const [openAddTaskModal, setOpenAddTaskModal] = useState(false);
  const [draftTaskDocid, setDraftTaskDocid] = useState(null); // docid of the draft task created on slot pick
  const [draftTask, setDraftTask] = useState(null); // local task state for TaskEditor
  const [editTaskModalOpen, setEditTaskModalOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);

  // Active task being edited in the team modal
  const [activeTask, setActiveTask] = useState(null);

  // Hooks
  // One useSettings() call — each invocation opens its own Firestore listeners.
  const {
    compagnyName: companyName,
    email: companyEmail,
    autoPlanHouseVisitStatus,
    defaultHouseVisitReminderPlan,
    appointmentKindsConfig,
    housevisits,
    agendaScheduleDisplay,
  } = useSettings();
  const adressId = Location()[3];
  const history = useHistory();
  const host = Hostname();
  // Soorten afspraak uit de instelling en het merk — geen tweede useSettings/Hostname.
  const kinds = useMemo(
    () => resolveAppointmentKinds({ AppointmentKinds: appointmentKindsConfig }, { brand: host?.Name || '' }),
    [appointmentKindsConfig, host],
  );
  const taskDTO = useTaskDTO();

  // Firestore
  // AI-CONTEXT: See docs/pitfalls.md#database-firestore-null-is-a-valid-query-value
  // `Parent: null` is bewust — dat selecteert de hoofdafspraken en sluit
  // subtaken uit. Niet vervangen door '' of weglaten.
  const tasks = useFirestoreGeneralTwoOrderBy(
    "Tasks",
    "AdressID",
    adressId ? adressId : "",
    'Parent',
      null,
    "Position",
    "desc"
  );
  const team = useFirestoreUsersApproved();
  const personas = useFirestoreGeneral("Personas", "Status", "active");
  const data = useFirestoreGeneralOrderBy(
    "PersonaData",
    "PersonaID",
    adressId ? adressId : "",
    "Position",
    "asc"
  );

  // Naam en e-mail van het huishouden: de vaste gegevens op het huishouden,
  // tot de migratie aangevuld uit de oude regels. readHouseholdField plakt
  // nooit twee waarden aan elkaar en controleert een oud e-mailadres.
  // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T7)
  const adressDocs = useFirestoreGeneral("Adresses", "ID", adressId || "");
  useEffect(() => {
    const adress = Array.isArray(adressDocs) ? adressDocs[0] : undefined;
    const rows = Array.isArray(data) ? data : undefined;
    setAdressName(readHouseholdField(adress, rows, "Naam") || "");
    setAdressEmail(readHouseholdField(adress, rows, "Email") || "");
  }, [adressDocs, data]);

  // ----- Date/Time helpers -----
  const pad2 = (n) => String(n).padStart(2, "0");

  const tsToDateValue = (ts) => {
    if (!ts) return "";
    const d = ts.toDate();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  };

  const tsToTimeValue = (ts) => {
    if (!ts) return "";
    const d = ts.toDate();
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  };

  const buildDateFromInputs = ({ dateStr, timeStr }) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("-").map(Number);
    const [hh, mm] = (timeStr || "00:00").split(":").map(Number);
    return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
  };

  const getFallbackDateStr = () => {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  };

  // Get team members list (team + personas)
  const teamMembers = useMemo(() => getTeamMembers(team, personas), [team, personas]);

  // Picker shows personas only — assigning team users is deprecated.
  const pickerMembers = useMemo(
    () => teamMembers.filter((m) => m.Type === "persona"),
    [teamMembers]
  );

  // Whenever Firestore data changes, update dragArray state
  useEffect(() => {
    setDragArray(tasks);
  }, [tasks]);

  // DRAG & DROP: reorder items and update Firestore
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArray(items);

    items.forEach((dragItem, idx) => {
      db.collection("Tasks").doc(dragItem.docid).update({ Position: idx + 1 });
    });

    setSaved("flex");
  };

  // ✅ Format: "donderdag 22 januari 2026 van 12.00 tot 13.00"
  const formatHousevisitSentence = (startTs, endTs, timeZone = "Europe/Amsterdam") => {
    if (!startTs || !endTs) return "";

    const start = startTs.toDate ? startTs.toDate() : new Date(startTs);
    const end = endTs.toDate ? endTs.toDate() : new Date(endTs);

    const weekdayDate = new Intl.DateTimeFormat("nl-NL", {
      timeZone,
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(start);

    const startTime = new Intl.DateTimeFormat("nl-NL", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(start);

    const endTime = new Intl.DateTimeFormat("nl-NL", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(end);

    return `${weekdayDate} van ${startTime.replace(":", ".")} tot ${endTime.replace(":", ".")}`;
  };

  // Helper to send confirmation email when adding a task
  const sendConfirmationEmail = async ({ startTs, endTs }) => {
    const whenText = formatHousevisitSentence(startTs, endTs);
    // Kopie naar wie inplant of bevestigt, niet naar het algemene adres:
    // anders krijgt één medewerker elke bevestiging van zijn collega's.
    const { cc, replyTo } = confirmationMailAddresses({
      plannerEmail: auth?.Email,
      companyEmail,
    });

    // Geen bruikbaar e-mailadres: geen mail. Vroeger ging hij dan uit met
    // `to: ""` — naar niemand, zonder dat iemand het zag. Nu zegt het scherm het.
    // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T7)
    const mail = buildConfirmationMail({ adressName, adressEmail, companyName, whenText, replyTo });
    if (!mail) {
      setSavedMessage("Er is geen bevestigingsmail gestuurd: dit huishouden heeft geen geldig e-mailadres bij 'Vaste gegevens'.");
      return;
    }

    await db.collection("Email").doc().set({
      to: mail.to,
      // Het adres is altijd info@deccos.nl (de mailserver weigert elk ander
      // adres, stil), maar de bewoner ziet de organisatie als afzendernaam.
      // Antwoorden gaan via replyTo naar het adres van de organisatie.
      from: buildPlatformSender(companyName),
      replyTo,
      cc,
      message: mail.message,
    });
  };

  // Create task immediately when slot is picked — saved as draft
  /**
   * @param {Date} start
   * @param {Date} end
   * @param {string} [personaId] - Gekozen op de regel van een medewerker in de
   *   roosterweergave; die hoort dan meteen op de afspraak te staan.
   */
  const createDraftTask = async (start, end, personaId) => {
    const taskId = uuid();
    const startTs = firebase.firestore.Timestamp.fromDate(start);
    const endTs = firebase.firestore.Timestamp.fromDate(end);

    // Read from Firestore directly — the `data` hook may not have delivered
    // its first snapshot yet on fast slot-picks, which was dropping Huisnummer.
    // Adres en bewonersnaam gaan mee op de taak, zodat de agenda ze zonder
    // tweede lookup in het blok kan zetten.
    // AI-CONTEXT: See docs/patterns.md#data-denormalise-label-onto-response
    const labels = await getAdressLabels(adressId);

    const taskData = {
      ID: taskId,
      AdressID: adressId,
      Description: labels.description || adressTitle || "",
      AdressLabel: labels.adressLabel || adressTitle || "",
      AdressName: labels.adressName || adressName || "",
      // De standaardsoort van de organisatie als titel (of 'Huisbezoek').
      ...defaultVisitTitle(kinds),
      Tags: [],
      CompagnyID: client,
      Compagny: client,
      Position: tasks?.length ? tasks.length + 1 : 1,
      Timestamp: startTs,
      TimestampEnd: endTs,
      User: auth?.ID || "",
      TeamMembers: personaId ? [personaId] : [],
      Type: "addressVisit",
      Status: "draft",
      Parent: null,
      rrule: null,
      exdate: [],
      exrule: null,
    };

    const ref = await db.collection("Tasks").add(taskData);
    setDraftTaskDocid(ref.id);
    setDraftTask({ ...taskData, docid: ref.id });
    setSaved("flex");
  };

  // Called on modal "Opslaan" — sends confirmation email and marks task confirmed
  const confirmDraftTask = async () => {
    if (!draftTaskDocid || !draftTask) return;

    await sendConfirmationEmail({ startTs: draftTask.Timestamp, endTs: draftTask.TimestampEnd });
    await db.collection("Tasks").doc(draftTaskDocid).update({ Status: "confirmed" });
    await nabehandeling(draftTask);

    setDraftTaskDocid(null);
    setDraftTask(null);
    setSaved("flex");
  };

  // Title handler
  const titleHandler = async (e) => {
    const docid = e.target.dataset.docid;
    const value = e.target.value;

    await db.collection("Tasks").doc(docid).update({ Title: value });
    setSaved("flex");
  };

  // ----- Date / Start / End handlers -----
  const dateHandler = async (e, task) => {
    const dateStr = e.target.value;

    const startTimeStr = tsToTimeValue(task.Timestamp);
    const endTimeStr = tsToTimeValue(task.TimestampEnd);

    const startDate = buildDateFromInputs({ dateStr, timeStr: startTimeStr || "00:00" });
    const endDate = endTimeStr ? buildDateFromInputs({ dateStr, timeStr: endTimeStr }) : null;

    await db.collection("Tasks").doc(task.docid).update({
      Timestamp: startDate ? firebase.firestore.Timestamp.fromDate(startDate) : null,
      TimestampEnd: endDate ? firebase.firestore.Timestamp.fromDate(endDate) : null,
    });

    setSaved("flex");
  };

  const startTimeHandler = async (e, task) => {
    const timeStr = e.target.value;

    const dateStr =
      tsToDateValue(task.Timestamp) || tsToDateValue(task.TimestampEnd) || getFallbackDateStr();

    const startDate = buildDateFromInputs({ dateStr, timeStr });

    await db.collection("Tasks").doc(task.docid).update({
      Timestamp: startDate ? firebase.firestore.Timestamp.fromDate(startDate) : null,
    });

    setSaved("flex");
  };

  const endTimeHandler = async (e, task) => {
    const timeStr = e.target.value;

    if (!timeStr) {
      // voor bestaande taken mag je dit eventueel blokkeren als je ook daar verplicht wilt afdwingen
      await db.collection("Tasks").doc(task.docid).update({ TimestampEnd: null });
      setSaved("flex");
      return;
    }

    const dateStr =
      tsToDateValue(task.Timestamp) || tsToDateValue(task.TimestampEnd) || getFallbackDateStr();

    const endDate = buildDateFromInputs({ dateStr, timeStr });

    await db.collection("Tasks").doc(task.docid).update({
      TimestampEnd: endDate ? firebase.firestore.Timestamp.fromDate(endDate) : null,
    });

    setSaved("flex");
  };

  /**
   * Na het bevestigen, voor beide knoppen: status op 'Ingepland' en de
   * automatische herinnering(en), zoals ingesteld. Wat niet kon (geen adres,
   * geen nummer, geen SMS-toestemming) komt als melding.
   * @param {object} task
   */
  const nabehandeling = async (task) => {
    const notices = await afterHouseVisitConfirmed({
      task,
      authId: auth?.ID,
      autoPlan: autoPlanHouseVisitStatus,
      reminderPlan: defaultHouseVisitReminderPlan,
    });
    if (notices.length > 0) setSavedMessage(notices.join('. '));
  };

  // Een concept dat al in de lijst stond bevestigen — hetzelfde als het
  // concept uit het venster, dus ook dezelfde nabehandeling. Tot 2026-10-01
  // miste dit pad die: wel de mail, geen status en geen herinnering.
  const confirmTask = async (task) => {
    await sendConfirmationEmail({ startTs: task.Timestamp, endTs: task.TimestampEnd });
    await db.collection("Tasks").doc(task.docid).update({ Status: "confirmed" });
    await nabehandeling(task);
    setSaved("flex");
  };

  // Delete modal
  // AI-CONTEXT: See docs/pitfalls.md#state-event-target-vs-currenttarget
  const deleteModalHandler = (e) => {
    const docid = e.currentTarget.dataset.docid;
    const deleteName = e.currentTarget.dataset.deletename;

    setDeleteDocid(docid);
    setDeleteName(deleteName);
    setDeleteModal(!deleteModal);
  };

  // Delete task
  const deleteNote = () => {
    softDeleteDoc({ collection: 'Tasks', docid: deleteDocid })
      .then(() => setSaved("flex"));
  };

  // Helper to get member role name
  const memberRole = (member) => {
    if (member.Type === "team" || member.Type === "persona") {
      return <UserRoleMeta userRoleId={member.Role} />;
    }
    return "Geen rol gevonden";
  };

  return (
    <div style={{ display: adressNavigation === "tasks" ? "block" : "none" }}>


      <div className="persona-option-container">
        <ReadOnlyGuard edit="addresses.edit">
          {(canEdit) => (
          <Table
            columns={[
              { key: "drag", label: "", searchable: false },
              { key: "title", label: "TAAK" },
              { key: "date", label: "DATUM", searchable: false },
              { key: "start", label: "START", searchable: false },
              { key: "end", label: "EIND", searchable: false },
              { key: "team", label: "TEAM", searchable: false },
              { key: "confirm", label: "BEVESTIGEN", searchable: false },
              { key: "delete", label: "VERWIJDEREN", searchable: false },
            ]}
            items={dragArray || []}
            getItemKey={(task) => task.ID}
            renderRow={(task) => (
            <>
              <TableCell>
                <DragIndicatorOutlinedIcon />
              </TableCell>
              <TableTitleCell
                value={task.Title}
                onTitleClick={canEdit ? () => {
                  setEditTask(task);
                  setEditTaskModalOpen(true);
                } : undefined}
                onBlur={titleHandler}
                docid={task.docid}
                placeholder="Typ een titel"
              />
              <TableCell>
                <input
                  type="date"
                  value={tsToDateValue(task.Timestamp)}
                  onChange={(e) => dateHandler(e, task)}
                />
              </TableCell>
              <TableCell>
                <input
                  type="time"
                  value={tsToTimeValue(task.Timestamp)}
                  onChange={(e) => startTimeHandler(e, task)}
                />
              </TableCell>
              <TableCell>
                <input
                  type="time"
                  value={tsToTimeValue(task.TimestampEnd)}
                  onChange={(e) => endTimeHandler(e, task)}
                />
              </TableCell>
              <TableCell>
                <ul>
                  {task.TeamMembers &&
                    task.TeamMembers.map((memberId) => (
                      <li key={memberId}>
                        {teamMembers
                          .filter((member) => member.ID === memberId)
                          .map((member) => member.Name)
                          .join(", ")}
                      </li>
                    ))}
                </ul>
              </TableCell>
              <TableCell>
                {task.Type === "addressVisit" ? (
                  task.Status === "confirmed" ? (
                    <span className="task-status-badge task-status-confirmed">Bevestigd</span>
                  ) : (
                    <button
                      className="task-confirm-button"
                      onClick={() => confirmTask(task)}
                    >
                      Bevestig
                    </button>
                  )
                ) : null}
              </TableCell>
              <TableCell>
                {canEdit && (
                  <Tooltip content={"Taak verwijderen"} top="-60px" width={"30px"}>
                    <DeleteOutlinedIcon
                      className="table-delete-icon"
                      data-id={task.ID}
                      data-docid={task.docid}
                      data-deletename={task.Title}
                      onClick={deleteModalHandler}
                    />
                  </Tooltip>
                )}
              </TableCell>
            </>
            )}
            searchAccessor={(task) => task?.Title || ""}
            searchPlaceholder="Zoeken op taak"
            emptyMessage="Geen taken gevonden."
            draggable
            droppableId="adress-tasks"
            onDragEnd={handleDragEnd}
            addRow={canEdit ? {
              toolTipContent: "Taak toevoegen",
              onClick: () => {
                setDraftTaskDocid(null);
                setDraftTask(null);
                setOpenAddTaskModal(true);
              },
            } : undefined}
          />
          )}
        </ReadOnlyGuard>

        <DeleteModal
          deleteModal={deleteModal}
          setDeleteModal={setDeleteModal}
          deleteItem={deleteNote}
          deleteName={deleteName}
          softDelete
        />
      </div>

      <Modal
        modalOpen={teamMemberModal}
        setModalOpen={setTeamMemberModal}
        confirm={() => setTeamMemberModal(false)}
        pageHeader="Teamlid toevoegen"
        content={
          <ReadOnlyGuard edit="addresses.edit">
            <SelectTeamMembers
              taskDocid={activeTask?.docid || ""}
              teamMembers={pickerMembers}
              selectedIds={activeTask?.TeamMembers || []}
              memberRole={memberRole}
              onSaved={() => setSaved("flex")}
            />
          </ReadOnlyGuard>
        }
      />

      <Modal
        modalOpen={editTaskModalOpen}
        setModalOpen={setEditTaskModalOpen}
        confirm={() => setEditTaskModalOpen(false)}
        pageHeader="Afspraak bewerken"
        content={
          editTask ? (
            <ReadOnlyGuard edit="addresses.edit">
              {(canEdit) => (
                <TaskEditor
                  task={editTask}
                  action="edit"
                  onTaskChange={canEdit ? async (nextTask) => {
                    setEditTask(nextTask);
                    await taskDTO.save(nextTask);
                  } : () => {}}
                />
              )}
            </ReadOnlyGuard>
          ) : null
        }
      />

      <Modal
        modalOpen={openAddTaskModal}
        setModalOpen={setOpenAddTaskModal}
        confirm={async () => {
          await confirmDraftTask();
          setOpenAddTaskModal(false);
        }}
        confirmDisabled={!draftTaskDocid}
        pageHeader="Afspraak toevoegen"
        content={
          <ReadOnlyGuard edit="addresses.edit">
            {(canEdit) => (
              draftTask ? (
                <TaskEditor
                  task={draftTask}
                  action="edit"
                  onTaskChange={canEdit ? async (nextTask) => {
                    setDraftTask(nextTask);
                    await taskDTO.save(nextTask);
                  } : () => {}}
                />
              ) : (
                <AddTaskModalContent
                  onSlotSelect={createDraftTask}
                  // Rooster als achtergrond: uit de useSettings hierboven, zodat
                  // het venster er geen eigen listeners voor opent.
                  fixtool={householdPlanningEnabled({ housevisits })}
                  scheduleSetting={agendaScheduleDisplay}
                />
              )
            )}
          </ReadOnlyGuard>
        }
      />
    </div>
  );
};

export default Tasks;
